import { useState } from 'react';
import { useFormSubmissions, type FormSubmissionRow } from '../../../api/forms.api';
import type { FormFieldDefinition } from '@engage/types';
import { Btn, Icon, ICONS } from '../ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../ui/tokens';

/**
 * In-app review surface for completed forms. Lists the most recent submissions
 * and expands each to show the answered fields. This is "where completed forms
 * go for review" — alongside the contact each submission creates/updates and
 * the digest email sent to the owner.
 */
export function SubmissionsModal(props: {
  formId: string;
  fields: FormFieldDefinition[];
  onClose: () => void;
}) {
  const { data, isLoading } = useFormSubmissions(props.formId);
  const submissions = data ?? [];
  // Map field key → human label so the review reads nicely.
  const labelByKey = new Map(props.fields.map((f) => [f.key, f.label]));

  return (
    <div
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,24,60,.32)',
        backdropFilter: 'blur(1px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '60px 24px',
        zIndex: 200,
        fontFamily: FORM_UI_FONT,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: '100%',
          maxHeight: '80vh',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,18,60,.28)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.indigo, margin: 0 }}>Submissions</h2>
            <div style={{ fontSize: 12.5, color: C.sub, marginTop: 3 }}>
              {isLoading ? 'Loading…' : `${submissions.length} most recent · newest first`}
            </div>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: C.faint }}
          >
            <Icon d={ICONS.x} size={20} color={C.faint} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '12px 24px 20px' }}>
          {isLoading ? (
            <p style={{ fontSize: 13, color: C.sub, padding: '16px 0' }}>Loading submissions…</p>
          ) : submissions.length === 0 ? (
            <div
              style={{
                padding: 28,
                textAlign: 'center',
                border: `2px dashed ${C.line}`,
                borderRadius: 12,
                color: C.sub,
                margin: '12px 0',
              }}
            >
              <Icon d={ICONS.forms} size={28} color={C.faint} />
              <div style={{ fontSize: 13.5, fontWeight: 600, color: C.indigoSoft, marginTop: 10 }}>
                No submissions yet
              </div>
              <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>
                Completed forms appear here. Each one also creates or updates a contact and is
                included in the owner's digest email.
              </div>
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {submissions.map((s) => (
                <SubmissionRow key={s.id} submission={s} labelByKey={labelByKey} />
              ))}
            </ul>
          )}
        </div>

        <div style={{ padding: '12px 24px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'flex-end' }}>
          <Btn kind="primary" onClick={props.onClose}>Done</Btn>
        </div>
      </div>
    </div>
  );
}

function SubmissionRow({
  submission,
  labelByKey,
}: {
  submission: FormSubmissionRow;
  labelByKey: Map<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(submission.field_values ?? {});
  const when = new Date(submission.submitted_at).toLocaleString();

  return (
    <li style={{ border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '10px 12px',
          background: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontFamily: FORM_UI_FONT,
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{when}</span>
          <span style={{ fontSize: 11.5, color: C.faint }}>
            {submission.source} · {entries.length} field{entries.length === 1 ? '' : 's'}
            {submission.contact_id ? ' · linked to contact' : ''}
          </span>
        </span>
        <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .12s' }}>
          <Icon d={ICONS.chev} size={15} color={C.faint} />
        </span>
      </button>
      {open && (
        <div style={{ borderTop: `1px solid ${C.line}`, padding: '10px 12px', background: C.bg }}>
          {entries.length === 0 ? (
            <div style={{ fontSize: 12, color: C.faint }}>No field values recorded.</div>
          ) : (
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 6, columnGap: 12 }}>
              {entries.map(([key, value]) => (
                <div key={key} style={{ display: 'contents' }}>
                  <dt style={{ fontSize: 11.5, fontWeight: 600, color: C.sub }}>{labelByKey.get(key) ?? key}</dt>
                  <dd style={{ margin: 0, fontSize: 12.5, color: C.ink, wordBreak: 'break-word' }}>
                    {formatValue(value)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </li>
  );
}

function formatValue(v: unknown): string {
  if (v == null || v === '') return '-';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
